import React, { useState } from "react";

interface DurationInputProps {
  value: number | null;
  onChange: (seconds: number | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  inputClassName?: string;
  labelClassName?: string;
  showLabel?: boolean;
  alignLabel?: boolean;
  hideSpinner?: boolean;
}

const DurationInput: React.FC<DurationInputProps> = ({
  value,
  onChange,
  label = "Duration",
  placeholder = "0",
  required = false,
  error,
  inputClassName = "",
  labelClassName = "",
  showLabel = true,
  alignLabel = false,
  hideSpinner = false,
}) => {
  const getInitialUnitAndValue = (
    seconds: number | null
  ): { unit: "seconds" | "minutes" | "hours"; displayValue: string } => {
    if (seconds === null || seconds <= 0) {
      return { unit: "seconds", displayValue: "" };
    }
    if (seconds % 3600 === 0) {
      return { unit: "hours", displayValue: String(seconds / 3600) };
    }
    if (seconds % 60 === 0) {
      return { unit: "minutes", displayValue: String(seconds / 60) };
    }
    return { unit: "seconds", displayValue: String(seconds) };
  };

  const initial = getInitialUnitAndValue(value);
  const [unit, setUnit] = useState<"seconds" | "minutes" | "hours">(
    initial.unit
  );
  const [inputValue, setInputValue] = useState<string>(initial.displayValue);

  const calculateSeconds = (
    val: string,
    currentUnit: "seconds" | "minutes" | "hours"
  ): number | null => {
    const trimmed = val.trim();
    if (!trimmed) return null;
    const num = parseFloat(trimmed);
    if (isNaN(num) || num < 0) return null;

    let seconds: number;
    switch (currentUnit) {
      case "hours":
        seconds = num * 3600;
        break;
      case "minutes":
        seconds = num * 60;
        break;
      default:
        seconds = num;
    }
    return Math.round(seconds);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(calculateSeconds(val, unit));
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newUnit = e.target.value as "seconds" | "minutes" | "hours";
    setUnit(newUnit);
    onChange(calculateSeconds(inputValue, newUnit));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];
    if (allowedKeys.includes(e.key)) return;

    // Block letters
    if (/^[a-zA-Z]$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    // Block + - e E
    if (["+", "-", "e", "E"].includes(e.key)) {
      e.preventDefault();
      return;
    }

    // Allow numbers
    if (/^[0-9]$/.test(e.key)) return;

    if (unit === "seconds") {
      // No dot or comma for seconds
      if (e.key === "." || e.key === ",") {
        e.preventDefault();
      }
    } else {
      // For minutes/hours: block comma, block double dot
      if (e.key === ",") {
        e.preventDefault();
        return;
      }
      if (e.key === "." && inputValue.includes(".")) {
        e.preventDefault();
      }
    }
  };

  const baseInputClassName = `w-full border-none bg-white p-1 text-xs text-navy-700 placeholder:text-gray-400 outline-none dark:bg-navy-900 dark:text-white dark:placeholder:text-gray-700 md:p-2 md:text-sm ${inputClassName}`;

  const baseLabelClassName = `text-xs font-medium text-gray-700 dark:text-gray-300 md:text-sm ${labelClassName}`;

  return (
    <div className={`flex flex-col ${alignLabel ? "gap-1 md:gap-0" : "gap-1"}`}>
      {showLabel &&
        (alignLabel ? (
          <label
            className={`${baseLabelClassName} flex items-center md:min-h-[36px]`}
          >
            {label}
            {required && " *"}
          </label>
        ) : (
          <label className={baseLabelClassName}>
            {label}
            {required && " *"}
          </label>
        ))}
      <div
        className={`flex w-32 items-center rounded border bg-white px-2 dark:bg-navy-900 md:w-36 ${
          error
            ? "border-red-500 dark:border-red-400"
            : "border-gray-300 dark:border-gray-700"
        }`}
      >
        <input
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
          value={inputValue}
          onKeyDown={handleKeyDown}
          onChange={handleInputChange}
          className={`${baseInputClassName} ${
            hideSpinner
              ? "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              : ""
          }`}
        />
        <div className="relative flex items-center">
          <select
            value={unit}
            onChange={handleUnitChange}
            className="appearance-none border-none bg-white py-1 pl-1 pr-4 text-[10px] text-navy-700 outline-none dark:bg-navy-900 dark:text-white md:pl-2 md:pr-5 md:text-xs"
          >
            <option value="seconds" className="bg-white dark:bg-navy-900">
              Sec
            </option>
            <option value="minutes" className="bg-white dark:bg-navy-900">
              Min
            </option>
            <option value="hours" className="bg-white dark:bg-navy-900">
              Hr
            </option>
          </select>
          <svg
            className="pointer-events-none absolute right-0 h-3 w-3 text-gray-500 dark:text-gray-400 md:h-4 md:w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 md:text-sm">
          {error}
        </div>
      )}
    </div>
  );
};

export default DurationInput;
